import { AdvancedMarker, useAdvancedMarkerRef } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, memo } from "react";
import { useNavigate } from "react-router-dom";
import MediaSlideshow from "../../../media/components/MediaSlideshow.jsx";
import ThumbnailOnViewer from "../../../thumbnail/components/ThumbnailOnViewer";
import Button from '../../../../components/Button';
import { useProjectContext } from '../../../../hooks/useProjectContext';
import { markResetConversation } from '../../../aiChat/utils/aiChatUtils';
import { mapChromeShell } from '../../../../styles/mapChromeShell';
import { markerViewerShell } from '../../../../styles/markerViewerShell';

function PinSvg({ color }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className={mapChromeShell.pinSvg}
            aria-hidden="true"
        >
            <path
                d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                fill={color}
                stroke="#000"
                strokeWidth="1.2"
            />
        </svg>
    );
}

function MarkerItemComponent({ marker, color, onClick, clusterer }) {
    const validThumbnail = typeof marker.thumbnail_url === "string" && marker.thumbnail_url.trim() !== "";
    const [markerRef, markerEl] = useAdvancedMarkerRef();

    const handleMarkerClick = useCallback(() => {
        onClick?.(marker);
    }, [onClick, marker]);

    useEffect(() => {
        if (!markerEl || !clusterer?.current) return;
        const c = clusterer.current;
        c.addMarker(markerEl);
        return () => c.removeMarker(markerEl);
    }, [markerEl, clusterer]);

    if (validThumbnail) {
        return (
            <AdvancedMarker
                ref={markerRef}
                position={marker.position}
                title={marker.title || undefined}
                onClick={handleMarkerClick}
                className={`${mapChromeShell.markerItem} ${mapChromeShell.markerVisible}`}
            >
                <div className={mapChromeShell.thumbnailContainer}>
                    <ThumbnailOnViewer
                        url={marker.thumbnail_url}
                        alt={marker.title || "Marker Thumbnail"}
                        markerId={marker.id}
                        key={`thumbnail-${marker.id}-${marker.thumbnail_url}`}
                    />
                </div>
            </AdvancedMarker>
        );
    }

    return (
        <AdvancedMarker
            ref={markerRef}
            position={marker.position}
            title={marker.title || undefined}
            onClick={handleMarkerClick}
            className={`${mapChromeShell.markerItem} ${mapChromeShell.markerVisible}`}
        >
            <PinSvg color={color} />
        </AdvancedMarker>
    );
}

export const MarkerItem = memo(MarkerItemComponent, (prevProps, nextProps) => {
    return (
        prevProps.marker.id === nextProps.marker.id &&
        prevProps.marker.thumbnail_url === nextProps.marker.thumbnail_url &&
        prevProps.marker.position?.lat === nextProps.marker.position?.lat &&
        prevProps.marker.position?.lng === nextProps.marker.position?.lng &&
        prevProps.marker.title === nextProps.marker.title &&
        prevProps.color === nextProps.color &&
        prevProps.onClick === nextProps.onClick &&
        prevProps.clusterer === nextProps.clusterer
    );
});

export function MarkerViewer({ marker, label, mediaList, onEdit, canEdit, loading = false, onClose }) {
    return (
        <div className={markerViewerShell.container}>
            <MediaSlideshow
                key={`slideshow-${marker.id}-${loading}-${mediaList?.length}`}
                marker={marker}
                label={label}
                mediaList={mediaList}
                onEdit={onEdit}
                canEdit={canEdit}
                loading={loading}
                onClose={onClose}
            />
        </div>
    );
}

export function MarkerMetadata({ marker, label, onEdit, canEdit }) {
    const navigate = useNavigate();
    const { projectId } = useProjectContext();

    const handleAiChatClick = () => {
        if (projectId) {
            markResetConversation();
            navigate(`/dashboard/${projectId}/ai`, { state: { resetConversation: true } });
        }
    };

    return (
        <div className={markerViewerShell.metadata}>
            {(projectId || canEdit) && (
                <div className={markerViewerShell.buttonsWrap}>
                    {projectId && (
                        <Button
                            variant="tertiary"
                            size="lg"
                            onClick={handleAiChatClick}
                            className={markerViewerShell.aiChatButton}
                        >
                            <span className="material-symbols-outlined">
                                smart_toy
                            </span>
                            AI会話
                        </Button>
                    )}
                    {canEdit && (
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={onEdit}
                            className={markerViewerShell.editButton}
                        >
                            <span className="material-symbols-outlined">
                                edit
                            </span>
                            編集
                        </Button>
                    )}
                </div>
            )}

            <div className={markerViewerShell.mainInfo}>
                <div className={markerViewerShell.row}>
                    <div className={markerViewerShell.titleSection}>
                        <label className={markerViewerShell.viewerLabel}>タイトル</label>
                        <div className={markerViewerShell.viewerTitle}>
                            {marker.title || "（無題）"}
                        </div>
                    </div>

                    {label && (
                        <div className={markerViewerShell.labelSection}>
                            <label className={markerViewerShell.viewerLabel}>ラベル</label>
                            <div className={markerViewerShell.labelField}>
                                <span className={markerViewerShell.labelDot} style={{ background: label.color }} />
                                {label.name}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className={markerViewerShell.metaSection}>
                <label className={markerViewerShell.viewerLabel}>詳細情報</label>
                <div className={markerViewerShell.metaField}>
                    <div className={markerViewerShell.metaItem}>
                        <span className={markerViewerShell.metaLabel}>作成者:</span>
                        <span className={markerViewerShell.metaValue}>{marker.creatorName || "不明"}</span>
                    </div>
                    <div className={markerViewerShell.metaItem}>
                        <span className={markerViewerShell.metaLabel}>作成日:</span>
                        <span className={markerViewerShell.metaValue}>
                            {new Date(marker.createdAt).toLocaleString('ja-JP', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
